import { Tag } from '@components';
import { Box } from '@mui/material';
import { type FC, type PropsWithChildren, type ReactElement, useMemo } from 'react';

import Enums from '@core/types/api/Enums';
import type { User } from '@core/types/api/User/User';
import type { NestedKeyOf } from '@core/utils/types';
import { MarketCountBadge } from '@web/components';
import { ArchiveIconButton } from '@web/components/Archive/ArchiveIconButton';
import { DataTable } from '@web/components/DataTable/DataTable';
import type { ColumnConfig } from '@web/components/DataTable/TableDataModel';
import { useApiTableDataWithoutAggregations } from '@web/components/DataTable/useApiTableData';

import { useConfirmToggleUserActivation } from './useConfirmToggleUserActivation';

const MaybeDeactivatedFormatter: FC<PropsWithChildren<{ row: User }>> = ({ row, children }) => (
  <Box sx={row.isDeactivated ? { opacity: 0.5, filter: 'grayscale(1)' } : undefined}>
    {children}
  </Box>
);

const createUserTableColumnsColumnConfig = (): ColumnConfig<User, NestedKeyOf<User>>[] => {
  return [
    {
      id: 'firstName',
      label: 'Name',
      type: 'string',
      isDisplayed: true,
      formatValueForWeb: (_, row) => (
        <MaybeDeactivatedFormatter row={row}>{row.fullName()}</MaybeDeactivatedFormatter>
      ),
    },
    {
      id: 'roles',
      label: 'Roles',
      type: 'custom',
      isDisplayed: true,
      formatValueForWeb: (_, row) => (
        <MaybeDeactivatedFormatter row={row}>
          <Box gap={1} display='flex'>
            {row.roles.map((role) => (
              <Tag key={role.id} variant='Secondary' label={role.displayName()} />
            ))}
          </Box>
        </MaybeDeactivatedFormatter>
      ),
    },
    {
      id: 'marketSummaries',
      label: 'Markets',
      type: 'custom',
      isDisplayed: true,
      isSortingDisabled: true,
      formatValueForWeb: (_, row) => (
        <MaybeDeactivatedFormatter row={row}>
          <Box gap={1} display='flex'>
            <MarketCountBadge
              names={row.marketSummaries.map(({ name }) => name)}
              allMarkets={row.allMarkets}
            />
          </Box>
        </MaybeDeactivatedFormatter>
      ),
    },
    {
      id: 'emailAddress',
      label: 'Email',
      type: 'string',
      isDisplayed: true,
      formatValueForWeb: (_, row) => (
        <MaybeDeactivatedFormatter row={row}>{row.emailAddress}</MaybeDeactivatedFormatter>
      ),
    },
  ];
};

const isActivated = (user: User): boolean => !user.isDeactivated;

type Props = {
  editUser(user: User): void;
  tenantIdOverride?: string;
};

export const UserList: FC<Props> = ({ editUser, tenantIdOverride }) => {
  const { confirmAndToggle, isLoading: isToggling } = useConfirmToggleUserActivation({
    tenantIdOverride,
  });

  const columnConfigs = useMemo(() => createUserTableColumnsColumnConfig(), []);

  const userTableModel = useApiTableDataWithoutAggregations({
    rowsRouteKey: tenantIdOverride !== undefined ? 'GET users by tenant ID as admin' : 'GET users',
    rowsPathParams: { tenantID: tenantIdOverride },
    initialSortBy: {
      direction: Enums.SortDirection.Ascending,
      name: 'firstName',
    },
    extractRows: (usersList) => usersList.items,
    makeColumnConfigs: () => columnConfigs,
  });

  return (
    <DataTable
      tableName='Users'
      onEditPress={editUser}
      canEditRow={isActivated}
      tableModel={userTableModel}
      renderStickyActions={(user): ReactElement => (
        <ArchiveIconButton
          isArchived={user.isDeactivated}
          isLoading={isToggling}
          archiveLabel='Disable'
          restoreLabel='Enable'
          onClick={(): void => confirmAndToggle(user)}
        />
      )}
    />
  );
};
